import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import moment from "moment";

import ebiOfficeService from "api/ebiOfficeService";
import Button from "components/Button";
import HollowButton from "components/HollowButton";
import Input from "components/Input";
import Loading from "components/Loading";
import SecondCheckbox from "components/SecondCheckbox";
import { IEbiOfficeSettings } from "dto/Ebi/IEbiOffice";
import { IError } from "dto/IError";
import { EbiKeyShareRunStatus, processEbiKeyRequests, resetEbiKeyShareAvailability } from "utils/ebiShareKeys";
import { getErrorMessage } from "utils/getErrorMessage";
import { getUserId } from "utils/getUserId";
import { ENotificationType, notification } from "utils/notification";

import EbiAgentsSection from "./EbiAgentsSection";
import EbiConnectionSection from "./EbiConnectionSection";
import styles from "./styles.module.scss";
import { FC, IEbiSettingsForm } from "./typings";

// Elektroniczne Biuro Interesanta (EBI) — podzakładka „Podstawowe”: połączenie z platformą EBI (portal petenta poza
// urzędem), włączenie kanału „EBI”, adres portalu dla petentów (<adres portalu platformy>/<adres>), nazwa urzędu widoczna
// w portalu i w awizo (domyślnie z zakładki Urząd), kolejka udostępnień i usługi przeszyfrowań. Bez połączenia
// z platformą ustawienia są zablokowane. Zmienia administrator albo właściciel firmy; pozostali widzą ustawienia tylko
// do odczytu.

const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{1,62})[a-z0-9]$/;

const toForm = (settings?: IEbiOfficeSettings | null): IEbiSettingsForm => ({
  isEnabled: settings?.IsEnabled ?? false,
  slug: settings?.Slug ?? "",
  officeName: settings?.OfficeName ?? "",
  portalHeader: settings?.PortalHeader ?? "",
});

const EbiBasicTab: FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, isLoading } = ebiOfficeService.useGetSettings();
  const { mutate: save, isLoading: isSaving } =
    ebiOfficeService.useSaveSettings();

  const settings = data?.data ?? null;
  // BE sprzed wydzielenia platformy EBI nie zwraca IsConnected — wtedy bez blokady
  const isConnected = settings?.IsConnected !== false;
  const canEdit = (settings?.CanEdit ?? false) && isConnected;

  // Kolejka udostępnień petentom (etap 4): przesyłki, których klucz trzeba zawinąć dla klucza petenta. Obsługuje
  // ją automatycznie KeysManager w przeglądarce pracownika — tu licznik i ręczne uruchomienie.
  const auth = useAuth();
  const userId = getUserId(auth.user!);
  const { data: summaryData, refetch: refetchSummary } =
    ebiOfficeService.useGetKeyRequestsSummary(!!settings?.IsEnabled);
  const { mutate: retryFailed, isLoading: isRetrying } =
    ebiOfficeService.useRetryFailedKeyRequests();
  const [isSharing, setIsSharing] = useState(false);
  const summary = summaryData?.data;
  const waiting = (summary?.Pending ?? 0) + (summary?.InProgress ?? 0);

  const handleShareNow = async () => {
    if (!userId) return;
    setIsSharing(true);

    try {
      resetEbiKeyShareAvailability();
      const { status, shared } = await processEbiKeyRequests(userId);
      const messages: Record<EbiKeyShareRunStatus, string> = {
        done: t("ebiOffice.keyShare.shared", { count: shared }),
        idle: t("ebiOffice.keyShare.nothingShared"),
        busy: t("ebiOffice.keyShare.busy"),
        unavailable: t("ebiOffice.keyShare.unavailable"),
        keyMismatch: t("ebiOffice.keyShare.keyMismatch"),
        failed: t("ebiOffice.keyShare.runFailed", { count: shared }),
      };

      notification({
        type: status === "failed" || status === "keyMismatch" ? ENotificationType.ERROR : ENotificationType.INFO,
        title: t("ebiOffice.keyShare.title"),
        text: messages[status],
      });
    } finally {
      setIsSharing(false);
      refetchSummary();
    }
  };

  const handleRetry = () =>
    retryFailed(undefined, {
      onSuccess: () => refetchSummary(),
      onError: (e: IError) =>
        notification({
          type: ENotificationType.ERROR,
          title: t("ebiOffice.keyShare.title"),
          text: getErrorMessage(e) || t("errors.somethingWentWrongShort"),
        }),
    });

  const [form, setForm] = useState<IEbiSettingsForm>(() => toForm(settings));

  useEffect(() => {
    setForm(toForm(settings));
  }, [settings]);

  const slugError =
    form.slug.length > 0 && !SLUG_PATTERN.test(form.slug)
      ? t("ebiOffice.settings.slugInvalid")
      : null;

  const isDirty = useMemo(() => {
    const saved = toForm(settings);
    return (
      saved.isEnabled !== form.isEnabled ||
      saved.slug !== form.slug ||
      saved.officeName !== form.officeName ||
      saved.portalHeader !== form.portalHeader
    );
  }, [settings, form]);

  // Nazwa urzędu: pole może być puste, gdy nazwa jest wpisana w zakładce Urząd (BE: DefaultOfficeName).
  const defaultOfficeName = settings?.DefaultOfficeName?.trim() || "";
  const canSave =
    canEdit &&
    isDirty &&
    !slugError &&
    !!form.slug.length &&
    (!form.isEnabled || !!form.officeName.trim().length || !!defaultOfficeName);

  const handleSave = () => {
    save(
      {
        IsEnabled: form.isEnabled,
        Slug: form.slug,
        OfficeName: form.officeName.trim() || null,
        PortalHeader: form.portalHeader.trim() || null,
      },
      {
        onSuccess: ({ data: saved }) => {
          queryClient.setQueryData(ebiOfficeService.queryKeys.getSettings, {
            data: saved,
          });
          // kanał „EBI” w kreatorze i forma doręczenia w biurze podawczym zależą od włączenia
          queryClient.invalidateQueries("outcommingParcel");
          // nazwa nadawcy w podglądzie powiadomień
          queryClient.invalidateQueries("ebiOfficeService.getMailPreview");
          notification({
            type: ENotificationType.SUCCESS,
            title: t("ebiOffice.settings.title"),
            text: t("ebiOffice.settings.saved"),
          });
        },
        onError: (e: IError) =>
          notification({
            type: ENotificationType.ERROR,
            title: t("ebiOffice.settings.title"),
            text:
              getErrorMessage(e) || t("errors.somethingWentWrongShort"),
          }),
      },
    );
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Loading />
      </div>
    );
  }

  return (
    <div className={styles.column}>
      <EbiConnectionSection />

      <div className={styles.section}>
        <div className={styles.sectionTitle}>
          {t("ebiOffice.settings.title")}
        </div>
        <div className={styles.hint}>{t("ebiOffice.settings.description")}</div>
        {!isConnected ? (
          <div className={styles.hint}>{t("ebiOffice.connection.lockedBelow")}</div>
        ) : (
          !canEdit && <div className={styles.hint}>{t("ebiOffice.settings.readOnly")}</div>
        )}
      </div>

      <div className={styles.section}>
        <SecondCheckbox
          value={form.isEnabled}
          onChange={(isEnabled) => canEdit && setForm({ ...form, isEnabled })}
          text={t("ebiOffice.settings.enabled")}
        />

        <div className={styles.field}>
          <Input
            value={form.slug}
            onChange={(value) =>
              setForm({
                ...form,
                slug: value.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 64),
              })
            }
            label={t("ebiOffice.settings.slug")}
            disabled={!canEdit}
          />
          {slugError ? (
            <div className={styles.error}>{slugError}</div>
          ) : (
            <div className={styles.hint}>
              {t("ebiOffice.settings.slugHint")}
            </div>
          )}
        </div>

        <div className={styles.field}>
          <Input
            value={form.officeName}
            onChange={(officeName) => setForm({ ...form, officeName })}
            label={t("ebiOffice.settings.officeName")}
            disabled={!canEdit}
          />
          <div className={styles.hint}>
            {defaultOfficeName
              ? t("ebiOffice.settings.officeNameHint", { name: defaultOfficeName })
              : t("ebiOffice.settings.officeNameHintEmpty")}
          </div>
        </div>

        <Input
          value={form.portalHeader}
          onChange={(portalHeader) => setForm({ ...form, portalHeader })}
          label={t("ebiOffice.settings.portalHeader")}
          disabled={!canEdit}
        />

        {settings?.PortalUrl && (
          <div className={styles.portal}>
            <div className={styles.portalLabel}>
              {t("ebiOffice.settings.portalUrl")}
            </div>
            <a
              className={styles.portalLink}
              href={settings.PortalUrl}
              target="_blank"
              rel="noreferrer"
            >
              {settings.PortalUrl}
            </a>
          </div>
        )}
      </div>

      {canEdit && (
        <div className={styles.actions}>
          <Button
            text={t("common.save")}
            onClick={handleSave}
            disabled={!canSave || isSaving}
          />
        </div>
      )}

      {settings?.IsEnabled && summary && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>{t("ebiOffice.keyShare.title")}</div>
          <div className={styles.hint}>{t("ebiOffice.keyShare.description")}</div>

          <div className={styles.queue}>
            <div>{t("ebiOffice.keyShare.waiting", { count: waiting })}</div>
            {summary.OldestRequestedDate && waiting > 0 && (
              <div className={styles.hint}>
                {t("ebiOffice.keyShare.oldest", {
                  date: moment(summary.OldestRequestedDate).format("DD.MM.YYYY HH:mm"),
                })}
              </div>
            )}
            {summary.Failed > 0 && (
              <div className={styles.error}>
                {t("ebiOffice.keyShare.failed", { count: summary.Failed })}
              </div>
            )}
          </div>

          {summary.CanProcess ? (
            <div className={styles.actions}>
              {summary.Failed > 0 && (
                <HollowButton
                  text={t("ebiOffice.keyShare.retry")}
                  onClick={handleRetry}
                  disabled={isRetrying}
                />
              )}
              <Button
                text={t("ebiOffice.keyShare.shareNow")}
                onClick={handleShareNow}
                disabled={isSharing || waiting === 0}
              />
            </div>
          ) : (
            <div className={styles.hint}>{t("ebiOffice.keyShare.cannotProcess")}</div>
          )}
        </div>
      )}

      {settings?.IsEnabled && canEdit && <EbiAgentsSection enabled />}
    </div>
  );
};

export default EbiBasicTab;
